import React, { useEffect } from "react";
import { Modal, ModalHeader, ModalBody, Row, Col, Label, Button, Input, FormFeedback } from "reactstrap";
import { useFormik, FieldArray, FormikProvider } from "formik";
import * as Yup from "yup";
import Select from "react-select";

import { selectCustomStyles } from "../../../common/selectCustomStyles";

const StudyLevelModal = ({ modal, toggle, isEdit, studyLevel, branches, semesters = [], academicCycleConfigs, onSubmit }) => {

    // 🔹 Préparer les options pour react-select
    const semesterOptions = semesters.map(semester => ({
        value: semester.id,
        label: semester.code
    }));

    // 🔹 Formik
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            id: studyLevel?.id || "",
            name: studyLevel?.name || "",
            branch_id: (studyLevel && studyLevel.branch_id) || '',
            academic_cycle_config_id: (studyLevel && studyLevel.academic_cycle_config_id) || '',
            study_level_semester_ids: studyLevel?.semesters ? studyLevel.semesters.map(s => s.id) : [],

            // Nom harmonisé pour correspondre au FieldArray
            academic_progression_rules: studyLevel?.academic_progression_rules?.length > 0
                ? studyLevel.academic_progression_rules
                : [{ start_semester: "", end_semester: "", credit_threshold_percentage: 70 }],
        },
        validationSchema: Yup.object({
            name: Yup.string().required("Veuillez saisir le nom"),
            branch_id: Yup.number().required("Veuillez sélectionner une filière"),
            academic_cycle_config_id: Yup.number().required("Veuillez sélectionner un cycle"),
            study_level_semester_ids: Yup.array().min(1, "Sélectionnez au moins un semestre").required("Champ obligatoire"),

            // Validation mise à jour avec le bon nom
            academic_progression_rules: Yup.array().of(
                Yup.object().shape({
                    start_semester: Yup.string().required("Requis"),
                    end_semester: Yup.string().required("Requis"),
                    credit_threshold_percentage: Yup.number().required("Requis").min(0).max(100),
                })
            )
        }),
        onSubmit: values => {
            const payload = {
                ...values,
                branch_id: Number(values.branch_id),
                academic_cycle_config_id: Number(values.academic_cycle_config_id),
                study_level_semester_ids: values.study_level_semester_ids.map(Number),

                // Transformation du tableau des règles
                academic_progression_rules: values.academic_progression_rules.map((rule) => ({
                    // On garde les noms des semestres (ou IDs si ton API attend des IDs)
                    start_semester: rule.start_semester, 
                    end_semester: rule.end_semester,
                    // On force le seuil en nombre entier/flottant
                    credit_threshold_percentage: parseFloat(rule.credit_threshold_percentage) || 0
                }))
            };
            onSubmit(payload);
        }
    });

    return (
        <Modal isOpen={modal} toggle={toggle} backdrop="static">
            <ModalHeader toggle={toggle}>
                {isEdit ? "Modifier - Niveau d'étude" : "Ajouter - Niveau d'étude"}
            </ModalHeader>
            <ModalBody>
                {/* 🔹 PROVIDER POUR FIELDARRAY */}
                <FormikProvider value={formik}>
                    <form onSubmit={formik.handleSubmit}>
                        <Row>
                            <Col className="col-12">
                                <div className="mb-3">
                                    <Label>Nom</Label>
                                    <input
                                        type="text"
                                        name="name"
                                        className={`form-control ${formik.touched.name && formik.errors.name ? "is-invalid" : ""}`}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.name}
                                        placeholder="Nom"
                                    />
                                    {formik.touched.name && formik.errors.name && (
                                        <div className="invalid-feedback">{formik.errors.name}</div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <Label>Filière</Label>
                                    <Input
                                        name="branch_id"
                                        type="select"
                                        className="form-select"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.branch_id || ""}
                                        invalid={formik.touched.branch_id && formik.errors.branch_id ? true : false}
                                    >
                                        <option value="">-- Sélectionner --</option>
                                        {branches.map((c) => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </Input>
                                    {formik.touched.branch_id && formik.errors.branch_id ? (
                                        <FormFeedback type="invalid">{formik.errors.branch_id}</FormFeedback>
                                    ) : null}
                                </div>

                                <div className="mb-3">
                                    <Label>Semestres</Label>
                                    <Select
                                        name="study_level_semester_ids"
                                        styles={selectCustomStyles}
                                        options={semesterOptions}
                                        isMulti
                                        value={semesterOptions.filter(option =>
                                            formik.values.study_level_semester_ids.includes(option.value)
                                        )}
                                        onChange={selectedOptions => {
                                            const values = selectedOptions ? selectedOptions.map(o => o.value) : [];
                                            formik.setFieldValue("study_level_semester_ids", values);
                                        }}
                                        onBlur={() => formik.setFieldTouched("study_level_semester_ids", true)}
                                    />
                                    {formik.touched.study_level_semester_ids && formik.errors.study_level_semester_ids && (
                                        <div className="text-danger mt-1">{formik.errors.study_level_semester_ids}</div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <Label>Cycle Académique</Label>
                                    <Input
                                        name="academic_cycle_config_id"
                                        type="select"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.academic_cycle_config_id}
                                        invalid={formik.touched.academic_cycle_config_id && !!formik.errors.academic_cycle_config_id}
                                    >
                                        <option value="">Sélectionner un cycle...</option>
                                        {academicCycleConfigs.map((cycle) => (
                                            <option key={cycle.id} value={cycle.id}>
                                                {cycle.name}
                                            </option>
                                        ))}
                                    </Input>
                                    {formik.touched.academic_cycle_config_id && formik.errors.academic_cycle_config_id && (
                                        <FormFeedback>{formik.errors.academic_cycle_config_id}</FormFeedback>
                                    )}
                                </div>

                                <hr className="my-4" />

                                <FieldArray name="academic_progression_rules">
                                    {({ push, remove }) => (
                                        <fieldset className="border p-3 rounded mb-3">
                                            <legend className="w-auto px-2 fs-6 fw-bold text-primary d-flex align-items-center" style={{ float: 'none', marginBottom: 0 }}>
                                                Règles de progression
                                                <Button
                                                    type="button"
                                                    color="primary"
                                                    size="sm"
                                                    className="ms-3 btn-rounded"
                                                    onClick={() => push({ start_semester: "", end_semester: "", credit_threshold_percentage: 70 })}
                                                >
                                                    <i className="mdi mdi-plus me-1"></i> Ajouter
                                                </Button>
                                            </legend>

                                            <p className="text-muted small mb-3">
                                                Définissez les seuils de validation par crédit pour ce niveau.
                                            </p>

                                            {formik.values.academic_progression_rules.map((rule, index) => (
                                                <div key={index} className="bg-light p-2 rounded mb-2 border position-relative">
                                                    <Row className="align-items-end g-2">
                                                        <Col md="4">
                                                            <Label className="form-label small mb-1 text-dark">Semestre Début</Label>
                                                            <Input
                                                                name={`academic_progression_rules.${index}.start_semester`}
                                                                type="select"
                                                                className="form-select-sm"
                                                                onChange={formik.handleChange}
                                                                value={rule.start_semester}
                                                                invalid={formik.touched.academic_progression_rules?.[index]?.start_semester && !!formik.errors.academic_progression_rules?.[index]?.start_semester}
                                                            >
                                                                <option value="">Choisir...</option>
                                                                {semesters.map(s => <option key={s.id} value={s.code}>{s.code}</option>)}
                                                            </Input>
                                                        </Col>

                                                        <Col md="4">
                                                            <Label className="form-label small mb-1 text-dark">Semestre Fin</Label>
                                                            <Input
                                                                name={`academic_progression_rules.${index}.end_semester`}
                                                                type="select"
                                                                className="form-select-sm"
                                                                onChange={formik.handleChange}
                                                                value={rule.end_semester}
                                                                invalid={formik.touched.academic_progression_rules?.[index]?.end_semester && !!formik.errors.academic_progression_rules?.[index]?.end_semester}
                                                            >
                                                                <option value="">Choisir...</option>
                                                                {semesters.map(s => <option key={s.id} value={s.code}>{s.code}</option>)}
                                                            </Input>
                                                        </Col>

                                                        <Col md="3">
                                                            <Label className="form-label small mb-1 text-dark">Seuil (%)</Label>
                                                            <Input
                                                                name={`academic_progression_rules.${index}.credit_threshold_percentage`}
                                                                type="number"
                                                                className="form-control-sm"
                                                                placeholder="70"
                                                                onChange={formik.handleChange}
                                                                value={rule.credit_threshold_percentage}
                                                            />
                                                        </Col>

                                                        <Col md="1" className="text-center">
                                                            {formik.values.academic_progression_rules.length > 1 && (
                                                                <Button
                                                                    type="button"
                                                                    color="link"
                                                                    className="text-danger p-0 mb-1"
                                                                    onClick={() => remove(index)}
                                                                >
                                                                    <i className="mdi mdi-delete-outline fs-4"></i>
                                                                </Button>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                </div>
                                            ))}
                                        </fieldset>
                                    )}
                                </FieldArray>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <div className="text-end">
                                    <Button type="submit" color="primary">
                                        {isEdit ? "Modifier" : "Enregistrer"}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </form>
                </FormikProvider>
            </ModalBody>
        </Modal>
    );
};

export default StudyLevelModal;